import { useEffect, useRef } from 'react';

import {
  CLAUSE_MATCH_STATES,
  type ClauseMatchListItem,
  type ClauseMatchState,
  type LibraryClause,
  type ManageClauseRequest,
} from '@clm/shared-types';

import type { ApiProblemKind } from '../../api/problem';
import { ClausePicker } from './ClausePicker';

/**
 * The paragraph matches row: three verbs and the inline confirmation, plus the
 * write-outcome notice (CLM-60, FR-28). Split out of `ParagraphMatches.tsx` so
 * the list/paging/write orchestration and the row presentation each stay near
 * the ~400-line ceiling. `resolveBody` is the one place the three verbs become
 * the one `resolve_clause_match` body — see the doc comment on the parent.
 */

export type Verb = 'confirm' | 'correct' | 'bespoke';

export interface Problem {
  readonly kind: ApiProblemKind;
  readonly detail: string | null;
  readonly correlation_id: string | null;
  readonly field_errors: readonly { readonly path: string; readonly message: string }[];
}

/** The one body every verb posts, built from the row and what the reviewer chose. */
function resolveBody(
  row: ClauseMatchListItem,
  verb: Verb,
  choice: { readonly picked: LibraryClause | null; readonly state: ClauseMatchState; readonly note: string },
): Extract<ManageClauseRequest, { action: 'resolve_clause_match' }> | null {
  const note = choice.note.trim();
  if (verb === 'confirm') {
    if (row.candidate === null) return null;
    const match_state = row.match_state ?? 'Standard';
    const deviation_note = match_state === 'Modified' ? (row.deviation_note ?? (note === '' ? null : note)) : null;
    if (match_state === 'Modified' && deviation_note === null) return null;
    return { action: 'resolve_clause_match', contract_clause_id: row.contract_clause_id, clause_code: row.candidate.clause_code, match_state, deviation_note };
  }
  if (verb === 'correct') {
    if (choice.picked === null) return null;
    if (choice.state === 'Modified' && note === '') return null;
    return {
      action: 'resolve_clause_match',
      contract_clause_id: row.contract_clause_id,
      clause_code: choice.picked.clause_code,
      match_state: choice.state,
      deviation_note: note === '' ? null : note,
    };
  }
  if (row.candidate === null) return null;
  return { action: 'resolve_clause_match', contract_clause_id: row.contract_clause_id, clause_code: null, match_state: null, deviation_note: null };
}

function decisionWord(body: Extract<ManageClauseRequest, { action: 'resolve_clause_match' }>): string {
  if (body.clause_code === null) return 'Marked bespoke';
  return `Corrected to ${body.clause_code}`;
}

/** The row's action cell: three verbs, then an inline "Confirm? [Yes] [No]" swap — no modal. */
export function RowActions({
  row,
  confirming,
  disabled,
  roleHint,
  note,
  picked,
  correctState,
  fieldErrors,
  onAsk,
  onCancel,
  onNote,
  onPick,
  onState,
  onConfirm,
}: {
  readonly row: ClauseMatchListItem;
  readonly confirming: Verb | null;
  readonly disabled: boolean;
  readonly roleHint: string | null;
  readonly note: string;
  readonly picked: LibraryClause | null;
  readonly correctState: ClauseMatchState;
  readonly fieldErrors: readonly { readonly path: string; readonly message: string }[];
  readonly onAsk: (verb: Verb) => void;
  readonly onCancel: () => void;
  readonly onNote: (note: string) => void;
  readonly onPick: (clause: LibraryClause) => void;
  readonly onState: (state: ClauseMatchState) => void;
  readonly onConfirm: (body: Extract<ManageClauseRequest, { action: 'resolve_clause_match' }>, word: string) => void;
}) {
  const yesRef = useRef<HTMLButtonElement | null>(null);
  const label = row.paragraph_ref ?? row.contract_clause_id;

  useEffect(() => {
    if (confirming !== null) yesRef.current?.focus();
  }, [confirming]);

  if (confirming === null) {
    const hint = roleHint === null ? {} : { title: roleHint };
    return (
      <span className="mr-actions">
        <button type="button" className="btn sm" disabled={disabled || row.candidate === null} aria-label={`Confirm paragraph ${label}`} {...hint} onClick={() => onAsk('confirm')}>
          Confirm
        </button>
        <button type="button" className="btn sm" disabled={disabled} aria-label={`Correct paragraph ${label}`} {...hint} onClick={() => onAsk('correct')}>
          Correct
        </button>
        {row.candidate === null ? (
          <button type="button" className="btn sm qt" disabled aria-label={`Paragraph ${label} is already bespoke`}>
            Already bespoke
          </button>
        ) : (
          <button type="button" className="btn sm" disabled={disabled} aria-label={`Bespoke paragraph ${label}`} {...hint} onClick={() => onAsk('bespoke')}>
            Bespoke
          </button>
        )}
      </span>
    );
  }

  const body = resolveBody(row, confirming, { picked, state: correctState, note });
  const word = confirming === 'confirm' ? 'Confirmed' : body === null ? '' : decisionWord(body);
  const needsNote = confirming === 'confirm' ? (row.match_state ?? 'Standard') === 'Modified' && row.deviation_note === null : confirming === 'correct';
  const question = confirming === 'confirm' ? 'Confirm?' : confirming === 'correct' ? 'Correct to which clause?' : 'Bespoke?';

  return (
    <div className="mr-confirm">
      {confirming === 'correct' ? <ClausePicker picked={picked} onPick={onPick} disabled={disabled} /> : null}
      {confirming === 'correct' ? (
        <div className="mr-radios" role="radiogroup" aria-label="Match state">
          {CLAUSE_MATCH_STATES.map((value) => (
            <label key={value} className="check">
              <input type="radio" name={`mr-state-${row.contract_clause_id}`} value={value} checked={correctState === value} disabled={disabled} onChange={() => onState(value)} />
              {value}
            </label>
          ))}
        </div>
      ) : null}
      {needsNote ? (
        <label className="check">
          Note
          <textarea
            className="mr-note"
            aria-label="Note"
            maxLength={2000}
            required={confirming === 'confirm' || correctState === 'Modified'}
            placeholder={confirming === 'confirm' || correctState === 'Modified' ? 'Required for Modified: what differs' : 'Optional'}
            value={note}
            disabled={disabled}
            onChange={(event) => onNote(event.target.value)}
          />
        </label>
      ) : null}
      {fieldErrors.map((entry) => (
        <p key={entry.path} className="mr-ferr" role="alert">
          {entry.path}: {entry.message}
        </p>
      ))}
      <span className="sq-alias-confirm">
        {question}{' '}
        <button
          ref={yesRef}
          type="button"
          className="btn sm pri"
          disabled={disabled || body === null}
          aria-label={`Yes, ${confirming} paragraph ${label}`}
          onClick={() => {
            if (body !== null) onConfirm(body, word);
          }}
        >
          Yes
        </button>{' '}
        <button type="button" className="btn sm qt" disabled={disabled} aria-label="No, leave it" onClick={onCancel}>
          No
        </button>
      </span>
    </div>
  );
}

export function WriteNotice({ problem }: { readonly problem: Problem }) {
  if (problem.kind === 'validation') {
    /* Field errors are shown inline in the open confirmation. */
    return null;
  }
  if (problem.kind === 'forbidden') {
    /* The sentence above the table says who may act. */
    return null;
  }
  if (problem.kind === 'conflict') {
    return (
      <div className="banner up" role="status">
        <p className="notice-head">
          <strong>Resolved by someone else — the list has been refreshed.</strong>
        </p>
        {problem.field_errors.map((entry) => (
          <p key={entry.path} className="mut">
            {entry.message}
          </p>
        ))}
      </div>
    );
  }
  if (problem.kind === 'not_found') {
    return (
      <div className="banner up" role="status">
        <p className="notice-head">
          <strong>Not found, or outside your scope — the list has been refreshed.</strong>
        </p>
      </div>
    );
  }
  return (
    <div className="banner bad" role="alert">
      <p className="notice-head">
        <strong>The decision was not recorded.</strong>
      </p>
      <p>{problem.detail ?? 'The request did not complete.'}</p>
      {problem.correlation_id === null ? null : <p className="mut">Correlation {problem.correlation_id}</p>}
    </div>
  );
}
